import { statusOptions } from "data/globalData";
import { attrGroupLinkedTables } from "components/PimDashboard/data";
import { sourcingOnlyColorColumnKeys } from "components/PimDashboard/DashboardComponents/Sourcing/FabricStylesDashboard/EditFabricStyleDetails/data";
import { validateMandatoryFields } from "utils/mandatoryFieldsLogic";

export const isColorRejected = (color) => color?.rejected === true || color?.status === statusOptions.REJECTED.key;

const MAIN_LINE_SEASON_PATTERN = /^(AW|SS)-\d{2}$/;
const STYLE_REF_REQUIRED_BRANDS = new Set(["RARE RABBIT", "RAREISM", "RARE ONES"]);
const STYLE_REF_REQUIRED_DIVISION = "APPARELS";
const STYLE_REF_NO_KEY = "style_ref_no";

/** Designer publish only. Keep in sync with requiresStyleRefNoOnDesignerPublish on the backend. */
const requiresStyleRefNoOnDesignerPublish = ({ brand, division, season } = {}) => {
	const normalizedBrand = String(brand ?? "").trim().toUpperCase();
	const normalizedDivision = String(division ?? "").trim().toUpperCase();
	const normalizedSeason = String(season ?? "").trim().toUpperCase();
	return (
		STYLE_REF_REQUIRED_BRANDS.has(normalizedBrand) &&
		normalizedDivision === STYLE_REF_REQUIRED_DIVISION &&
		MAIN_LINE_SEASON_PATTERN.test(normalizedSeason)
	);
};

/**
 * Mandatory attributes that are still empty for this style.
 * Same rules as the style publish modal: style, style details (except fabric_details),
 * and non-rejected colors. Duplicate keys are collapsed to one entry.
 */
export const getPendingRequiredFields = (styleDetails, allAttributes = []) => {
	if (!styleDetails || !allAttributes?.length) return [];

	const brand = styleDetails?.[attrGroupLinkedTables.style.key]?.brand || "";
	const category = styleDetails?.[attrGroupLinkedTables.style.key]?.category || "";

	const styleAttrs = allAttributes.filter((row) => row.linked_table === attrGroupLinkedTables.style.key);
	const styleDetailsAttrs = allAttributes.filter(
		(row) => row.linked_table === attrGroupLinkedTables.style_details.key && row.group_id !== "fabric_details",
	);

	const styleValidation = validateMandatoryFields(styleDetails, styleAttrs, brand, category, attrGroupLinkedTables.style.key);
	const styleDetailsValidation = validateMandatoryFields(
		styleDetails,
		styleDetailsAttrs,
		brand,
		category,
		attrGroupLinkedTables.style_details.key,
	);

	let pendingFields = [...styleValidation.missingFields, ...styleDetailsValidation.missingFields];

	styleDetails?.[attrGroupLinkedTables.color.key]?.forEach((colorRow) => {
		if (isColorRejected(colorRow)) return;

		const colorAttrs = allAttributes.filter(
			(row) => row.linked_table === attrGroupLinkedTables.color.key && !sourcingOnlyColorColumnKeys.includes(row.key),
		);

		const colorValidation = validateMandatoryFields(colorRow, colorAttrs, brand, category, null);
		pendingFields.push(...colorValidation.missingFields);
	});

	const styleRow = styleDetails?.[attrGroupLinkedTables.style.key] || {};
	if (requiresStyleRefNoOnDesignerPublish(styleRow)) {
		const missingStyleRefNo = styleDetails?.[attrGroupLinkedTables.color.key]?.some(
			(colorRow) => !isColorRejected(colorRow) && !String(colorRow?.style_ref_no ?? "").trim(),
		);
		if (missingStyleRefNo) {
			const styleRefAttr = allAttributes.find(
				(row) => row.key === STYLE_REF_NO_KEY && row.linked_table === attrGroupLinkedTables.color.key,
			);
			pendingFields.push(styleRefAttr || { key: STYLE_REF_NO_KEY, name: "Style Ref No" });
		}
	}

	return Object.values(
		pendingFields.reduce((acc, row) => {
			acc[row.key] = row;
			return acc;
		}, {}),
	);
};
